'use client'

import React from 'react'

import { Box, chakra } from '@chakra-ui/react'

import { StatusPill, StatusPillVariant } from '../Pill/StatusPill'
import {
  ColorScheme,
  statTileGroupStyles,
  statTileIconStyles,
  statTileLabelRowStyles,
  statTileStyles,
  statTileSubLabelStyles,
  statTileValueRowStyles,
  statTileValueStyles,
} from './StatTile.styles'

export type { ColorScheme }

// Compact pill so the flag sits on the value row without stretching it.
const FLAG_HEIGHT = '20px'
const FLAG_PX = '8px'

export type StatTileFlag = {
  variant: StatusPillVariant
  label: string
}

export type StatTileProps = {
  label: string
  value: React.ReactNode
  /** Icon rendered before the label. */
  icon?: React.ReactNode
  /** Muted copy after the value (e.g. "completed"). */
  subLabel?: string
  /** Status pill after the value calling out something that needs attention
   * (e.g. "2 incomplete"). */
  flag?: StatTileFlag
  /** Makes the whole tile a button. */
  onClick?: () => void
  /** Selected state for a tile that toggles content elsewhere (e.g. a
   * drill-down). Only applies with `onClick`. */
  isSelected?: boolean
  colorScheme?: ColorScheme
}

export const StatTile = ({
  label,
  value,
  icon,
  subLabel,
  flag,
  onClick,
  isSelected = false,
  colorScheme = 'dark',
}: StatTileProps) => {
  const isInteractive = onClick !== undefined
  const content = (
    <>
      <Box {...statTileLabelRowStyles(colorScheme)}>
        {icon && (
          <Box as='span' {...statTileIconStyles(colorScheme)}>
            {icon}
          </Box>
        )}
        <span>{label}</span>
      </Box>
      <Box {...statTileValueRowStyles}>
        <Box {...statTileValueStyles(colorScheme)}>{value}</Box>
        {subLabel && (
          <Box as='span' {...statTileSubLabelStyles(colorScheme)}>
            {subLabel}
          </Box>
        )}
        {flag && (
          <StatusPill
            colorScheme={colorScheme}
            h={FLAG_HEIGHT}
            label={flag.label}
            px={FLAG_PX}
            py={0}
            variant={flag.variant}
          />
        )}
      </Box>
    </>
  )

  return isInteractive ? (
    <chakra.button
      aria-pressed={isSelected}
      onClick={onClick}
      type='button'
      {...statTileStyles(colorScheme, true, isSelected)}
    >
      {content}
    </chakra.button>
  ) : (
    <Box {...statTileStyles(colorScheme)}>{content}</Box>
  )
}

export type StatTileGroupProps = {
  children: React.ReactNode
  /** Number of columns; defaults to the number of children (one row).
   * Tiles past the first row wrap onto new rows. */
  columns?: number
  colorScheme?: ColorScheme
}

/**
 * Number of tiles that reach the grid. Fragments render their children as
 * direct grid items, so they're counted through; `null`/`false` from
 * conditional tiles render nothing and aren't counted (`Children.toArray`
 * drops them, where `Children.count` would not).
 */
const countGridItems = (children: React.ReactNode): number =>
  React.Children.toArray(children).reduce<number>(
    (count, child) =>
      React.isValidElement<{ children?: React.ReactNode }>(child) &&
      child.type === React.Fragment
        ? count + countGridItems(child.props.children)
        : count + 1,
    0
  )

/** Lays `StatTile`s out in a bordered row with dividers between them. */
export const StatTileGroup = ({
  children,
  columns,
  colorScheme = 'dark',
}: StatTileGroupProps) => (
  <Box
    {...statTileGroupStyles(
      colorScheme,
      // At least one column so an empty group still yields a valid template.
      columns ?? Math.max(1, countGridItems(children))
    )}
  >
    {children}
  </Box>
)
